'use client'

import { FormBlock as FormBlockType, Form } from '@/payload-types'

import RichText from '@onclicks/sharedcomponents/RichText'
import { useRouter } from 'next/navigation'
import React, { useCallback, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '../components/ui/Button'

import { getClientSideURL } from '@/utilities/getURL'
import { fields } from '../components/FieldForm/fields'
import { cn } from '@/utilities/ui'
import { bagelFatOne, quicksand } from '@/utilities/fontImport'
import {
  PayloadRecaptcha,
  type RecaptchaHandle,
} from '@onclicks/recaptcha/components/PayloadRecaptcha'

export const FormBlock: React.FC<FormBlockType> = (props) => {
  const { enableIntro, form: formFromProps, introContent } = props

  const isFormObject = typeof formFromProps === 'object' && formFromProps !== null
  const form = isFormObject ? (formFromProps as Form & { requireRecaptcha?: boolean }) : null

  const formID = form?.id
  const confirmationMessage = form?.confirmationMessage
  const confirmationType = form?.confirmationType
  const redirect = form?.redirect
  const submitButtonLabel = form?.submitButtonLabel
  const requireRecaptcha = form?.requireRecaptcha

  const recaptcha = useRef<RecaptchaHandle>(null)

  const formMethods = useForm({
    defaultValues: form?.fields as unknown as Record<string, string>,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: Record<string, string>) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      if (!formID) return

      const submitForm = async () => {
        setError(undefined)

        const captchaValue = recaptcha.current ? recaptcha.current.getValue() : undefined

        if (recaptcha && !captchaValue && requireRecaptcha) {
          setIsLoading(false)
          setError({
            message: 'Veuillez valider le captcha.',
            status: '400',
          })
          return
        }

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
              recaptcha: captchaValue,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message:
                res.errors?.[0]?.message ||
                "Une erreur s'est produite, Veuillez réessayer plus tard.",
              status: res.status,
            })

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)
          recaptcha.current?.reset?.()

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: "Une erreur s'est produire, Veuillez réessayer plus tard.",
          })
          recaptcha.current?.reset?.()
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType, requireRecaptcha],
  )

  if (!form) return null

  return (
    <div
      className={cn(
        'flex h-full flex-col rounded-[2.5rem] border border-white p-8 shadow-xl shadow-white container lg:mr-10',
        quicksand.className,
        quicksand.variable,
        bagelFatOne.variable,
      )}
    >
      {enableIntro && introContent && !hasSubmitted && (
        <RichText
          className="form-intro mb-8 [&_h2]:font-(family-name:--font-bagel-fat-one) [&_p]:font-(family-name:--font-quicksand)"
          data={introContent}
          enableGutter={false}
          enableProse={false}
        />
      )}
      <div>
        <FormProvider {...formMethods}>
          {!isLoading && hasSubmitted && confirmationType === 'message' && confirmationMessage && (
            <RichText data={confirmationMessage} />
          )}
          {isLoading && !hasSubmitted && <p>Chargement, veuillez patienter...</p>}
          {!isLoading && error && (
            <div>
              {error.status ? `${error.status}: ` : ''}
              {error.message}
            </div>
          )}
          {!hasSubmitted && (
            <form id={formID?.toString()} onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4 flex flex-wrap gap-x-6 gap-y-4 last:mb-0">
                {form &&
                  form.fields &&
                  form.fields?.map((field, index) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    if (Field) {
                      const hasWidth = field && 'width' in field
                      const widthValue = hasWidth ? (field as any).width : 100
                      const isFullWidth = !widthValue || widthValue >= 100
                      const fieldWidth = isFullWidth ? '100%' : `calc(${widthValue}% - 0.75rem)`

                      return (
                        <div
                          key={index}
                          style={{
                            width: fieldWidth,
                            flexGrow: 0,
                            flexShrink: 0,
                            boxSizing: 'border-box',
                          }}
                        >
                          <Field
                            form={form}
                            {...field}
                            {...formMethods}
                            control={control}
                            errors={errors}
                            register={register}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
              </div>

              <div className="flex flex-col items-end gap-6 justify-center pb-18">
                <PayloadRecaptcha
                  ref={recaptcha}
                  requireRecaptcha={requireRecaptcha}
                  className="w-full sm:w-auto origin-top-left scale-[0.75] md:scale-100 -mb-6 md:mb-0"
                />

                <Button
                  className="px-10 py-3 mt-4 z-50"
                  form={formID?.toString()}
                  type="submit"
                  variant="gradientOrangeToPink"
                >
                  {submitButtonLabel}
                </Button>
              </div>
            </form>
          )}
        </FormProvider>
      </div>
    </div>
  )
}
